import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { CommandResult, ITranscriptEditorCommand } from './interfaces';
import { IBatchable } from './interfaces/IBatchable';
import { TranscriptContainer } from '../models/TranscriptContainer';

/**
 * Base class for all transcript commands
 * 
 * All transcript operations should be implemented as commands to enable
 * undo/redo functionality and consistent state tracking.
 */
export abstract class AbstractTranscriptEditorCommand implements ITranscriptEditorCommand, IBatchable {
  /** Human-readable description of the command */
  private description: string;
  
  /** Reference to the transcript editor core */
  protected editor: TranscriptEditorCore;
  
  /** Metadata for command tracking and debugging */
  protected metadata: Record<string, any> = {};
  
  /** Batch ID if part of a batch command */
  private batchId: string | null = null;
  
  /** Position within a batch command */
  private batchPosition: number | null = null;
  
  /** Flag indicating if this command needs sorting after execution */
  protected needsSorting: boolean = false;
  
  /** Flag indicating if this command needs heavy post-execution optimization */
  protected needsHeavyOptimization: boolean = true;
  
  /**
   * Create a new command
   * 
   * @param description Human-readable description of the command
   * @param editor Reference to the transcript editor core
   */
  constructor(description: string, editor: TranscriptEditorCore) {
    this.description = description;
    this.editor = editor;
  }
  
  /**
   * Get a human-readable description of the command
   * Useful for UI displays like undo/redo history
   * 
   * @returns The command description
   */
  getDescription(): string {
    return this.description;
  }
  
  /**
   * Execute the command
   * Should implement the actual command logic
   * 
   * @returns True if the command executed successfully, or a promise resolving to true
   */
  abstract execute(): CommandResult;
  
  /**
   * Undo the command
   * Should revert the state to what it was before execution
   * 
   * @returns True if the command was undone successfully, or a promise resolving to true
   */
  abstract undo(): CommandResult;
  
  /**
   * Sets metadata for the command
   * Useful for debugging and tracking
   * 
   * @param key Metadata key
   * @param value Metadata value
   */
  setMetadata(key: string, value: any): void {
    this.metadata[key] = value;
  }
  
  /**
   * Gets metadata for the command
   * 
   * @param key Metadata key
   * @returns Metadata value or undefined if not found
   */
  getMetadata(key: string): any {
    return this.metadata[key];
  }
  
  /**
   * Gets all metadata for the command
   * 
   * @returns All command metadata
   */
  getAllMetadata(): Record<string, any> {
    return { ...this.metadata };
  }
  
  /**
   * Set the batch ID this command belongs to
   * @param id The batch identifier
   */
  setBatchId(id: string): void {
    this.batchId = id;
  }
  
  /**
   * Set the position of this command within a batch
   * @param position Zero-based position within the batch
   */
  setBatchPosition(position: number): void {
    this.batchPosition = position;
  }
  
  /**
   * Get the batch ID this command belongs to
   * @returns The batch ID or null if not in a batch
   */
  getBatchId(): string | null {
    return this.batchId;
  }
  
  /**
   * Get the position of this command within a batch
   * @returns The position or null if not in a batch
   */
  getBatchPosition(): number | null {
    return this.batchPosition;
  }
  
  /**
   * Indicates whether segments and paragraphs should be sorted after this command is executed
   * Commands that modify segment or paragraph order should override this method
   * 
   * @returns True if segments/paragraphs should be sorted after execution, false otherwise
   */
  requiresSorting(): boolean {
    return this.needsSorting;
  }
  
  /**
   * Sets whether this command requires sorting after execution
   * 
   * @param value True if sorting is required, false otherwise
   */
  setRequiresSorting(value: boolean): void {
    this.needsSorting = value;
  }
  
  /**
   * Indicates whether this command needs heavy post-execution optimization
   * Commands like speaker assignments can skip these to improve performance
   * 
   * @returns True if heavy optimization is needed, false otherwise
   */
  getNeedsHeavyOptimization(): boolean {
    return this.needsHeavyOptimization;
  }
  
  /**
   * Sets whether this command needs heavy post-execution optimization
   * 
   * @param value True if heavy optimization is needed, false otherwise
   */
  setNeedsHeavyOptimization(value: boolean): void {
    this.needsHeavyOptimization = value;
  }
}